import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  connectBusinessPage,
  deleteBusinessPage,
  getAvailableBusinessPages,
  getBusinessPages,
  getLeaderPages,
  saveBusinessPageManual,
  selectBusinessPage,
  getReconciliationLogs,
} from "../../../services/settingsService";
import { hasStoredSessionToken, getStoredOrgId } from "../../../shared/authSession";
import logoImg from "../../../assets/SocialRipplelogo_J.png";
import { Auth } from "../../../contexts/AuthContext";
import { uploadMedia } from "../../../services/postService";
import OrganizationService from "../../../services/organizationService";
import UserService from "../../../services/categoryService";
import authService from "../../../services/authService";
import { platformApiRoot } from "../../../services/runtimeConfig";

const PLATFORM_ORDER = ["LINKEDIN", "X", "INSTAGRAM", "YOUTUBE", "FACEBOOK"];

const PLATFORM_LABELS = {
  LINKEDIN: "LinkedIn",
  X: "X",
  INSTAGRAM: "Instagram",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
};

const PLATFORM_OAUTH_DEFAULTS = {
  FACEBOOK: {
    oauthSupported: true,
    manualFallbackAvailable: false,
    oauthProviderLabel: "Meta",
    supportMessage:
      "Connect only organization-owned Facebook Pages through the shared Meta app. Personal Facebook profiles are not allowed.",
  },
  INSTAGRAM: {
    oauthSupported: true,
    manualFallbackAvailable: false,
    oauthProviderLabel: "Meta",
    supportMessage:
      "Connect only Instagram business or professional accounts linked to a Facebook Page. Personal Instagram accounts are not allowed.",
  },
  LINKEDIN: {
    oauthSupported: true,
    manualFallbackAvailable: true,
    oauthProviderLabel: "LinkedIn",
    supportMessage:
      "Connect your organization's LinkedIn company page. You must be an admin of the LinkedIn company page to connect it. Manual setup is also available as a fallback.",
  },
  X: {
    oauthSupported: true,
    manualFallbackAvailable: false,
    oauthProviderLabel: "X",
    supportMessage:
      "Connect the organization-owned X handle through the shared X app. After OAuth, an admin must confirm the connected handle before it is saved as the organization business account.",
  },
  YOUTUBE: {
    oauthSupported: true,
    manualFallbackAvailable: false,
    oauthProviderLabel: "Google",
    supportMessage: "Connect your organization's YouTube channel.",
  },
};

const accordionSx = {
  background: "#f5f5f5",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&::before": { display: "none" },
  "&.Mui-expanded": {
    margin: "0px !important",
    marginTop: "0px !important",
    marginBottom: "0px !important",
  },
  "& .MuiAccordionSummary-root": {
    width: "100%",
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    "&:hover .MuiTouchRipple-root": {
      display: "none",
    },
    "& .MuiButtonBase-root:hover": {
      backgroundColor: "transparent !important",
    },
  },
  "& .MuiButtonBase-root:hover": {
    backgroundColor: "transparent !important",
  },
};

const accordionSummarySx = {
  px: 2,
  pr: 2,
  width: "100%",
  boxSizing: "border-box",
  minHeight: "64px !important",
  "&:hover": {
    backgroundColor: "transparent !important",
  },
  "&.Mui-expanded": {
    minHeight: "64px !important",
  },
  "& .MuiAccordionSummary-content": {
    margin: "0 !important",
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: "0 !important",
  },
  "& .MuiTouchRipple-root": {
    display: "none",
  },
};

const accordionDetailsSx = {
  background: "#ffffff8f",
  borderRadius: "8px",
  mt: 1,
  p: 2,
};

const emptyPlatformState = (platform, capabilityKnown = true) => ({
  platform,
  connected: false,
  enabled: true,
  oauthSupported: capabilityKnown
    ? PLATFORM_OAUTH_DEFAULTS[platform]?.oauthSupported ?? false
    : null,
  manualFallbackAvailable: capabilityKnown
    ? PLATFORM_OAUTH_DEFAULTS[platform]?.manualFallbackAvailable ?? true
    : null,
  oauthProviderLabel: capabilityKnown
    ? PLATFORM_OAUTH_DEFAULTS[platform]?.oauthProviderLabel ?? ""
    : "",
  supportMessage: capabilityKnown
    ? PLATFORM_OAUTH_DEFAULTS[platform]?.supportMessage ?? ""
    : "",
  apiUrl: "",
  accessToken: "",
  accessTokenPreview: "",
  accessTokenConfigured: false,
  refreshToken: "",
  refreshTokenPreview: "",
  refreshTokenConfigured: false,
  externalUserId: "",
  pageId: "",
  username: "",
  pageUrl: "",
  businessPageLink: "",
  displayName: "",
  lastSyncAttemptAt: "",
  lastSyncSuccessAt: "",
  lastSyncStatus: "NEVER_SYNCED",
  lastSyncError: "",
  lastSyncErrorAt: "",
  lastImportedCount: null,
  scheduleCadenceLabel: "",
  nextScheduledFetchAt: "",
});

const formatSyncDateTime = (value) => {
  if (!value) {
    return "Never";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const getSyncStatusMeta = (page) => {
  if (!page.connected) {
    return {
      label: "Disconnected",
      color: "default",
      variant: "outlined",
    };
  }

  switch (page.lastSyncStatus) {
    case "IN_PROGRESS":
      return { label: "Syncing", color: "info", variant: "filled" };
    case "SUCCESS_WITH_NEW_CONTENT":
      return { label: "Synced", color: "success", variant: "filled" };
    case "SUCCESS_NO_NEW_CONTENT":
      return { label: "No New Content", color: "success", variant: "outlined" };
    case "SUCCESS":
      return { label: "Healthy", color: "success", variant: "filled" };
    case "FAILED":
      return { label: "Failed", color: "error", variant: "filled" };
    default:
      return { label: "Never Synced", color: "warning", variant: "outlined" };
  }
};

const getLeaderSyncStatusMeta = (platformStatus) => {
  const connected = platformStatus?.status === "CONNECTED";
  if (!connected) {
    return {
      label: "Disconnected",
      color: "default",
      variant: "outlined",
    };
  }

  switch (platformStatus?.lastSyncStatus) {
    case "IN_PROGRESS":
      return { label: "Syncing", color: "info", variant: "filled" };
    case "SUCCESS_WITH_NEW_CONTENT":
      return { label: "Synced", color: "success", variant: "filled" };
    case "SUCCESS_NO_NEW_CONTENT":
      return { label: "No New Content", color: "success", variant: "outlined" };
    case "SUCCESS":
      return { label: "Healthy", color: "success", variant: "filled" };
    case "FAILED":
      return { label: "Failed", color: "error", variant: "filled" };
    default:
      return { label: "Never Synced", color: "warning", variant: "outlined" };
  }
};

const getBusinessConnectErrorMessage = (platform, reason) => {
  const label = PLATFORM_LABELS[platform] || platform || "Business page";
  switch (reason) {
    case "transaction_expired":
      return `${label} connection expired. Start the business-page connection again.`;
    case "invalid_transaction":
      return `${label} connection could not be verified. Start the business-page connection again.`;
    case "token_exchange_failed":
      return `${label} connection failed while exchanging the provider token.`;
    case "page_discovery_failed":
      return `${label} connection failed while loading organization business pages.`;
    case "no_business_pages_found":
      return `${label} connection did not return any eligible organization business pages. Personal accounts are not allowed here.`;
    case "personal_account_not_allowed":
      return `${label} personal accounts are not allowed in business-page settings. Connect an organization-owned business page instead.`;
    case "manual_setup_required":
      return `${label} company pages must be connected manually using a valid organization access token and the numeric LinkedIn organization ID.`;
    case "unsupported_platform":
      return `${label} business-page connection is not available in this admin flow.`;
    default:
      return `${label} business page connection failed.`;
  }
};

const normalizePlatformCapabilities = (platform, item = {}, capabilityKnown = true) => {
  const defaults = PLATFORM_OAUTH_DEFAULTS[platform] || {};
  const normalized = {
    ...emptyPlatformState(platform, capabilityKnown),
    ...item,
  };

  if (capabilityKnown) {
    if (typeof normalized.oauthSupported !== "boolean") {
      normalized.oauthSupported = defaults.oauthSupported ?? false;
    }
    if (typeof normalized.manualFallbackAvailable !== "boolean") {
      normalized.manualFallbackAvailable =
        defaults.manualFallbackAvailable ?? true;
    }
    if (!normalized.oauthProviderLabel) {
      normalized.oauthProviderLabel = defaults.oauthProviderLabel ?? "";
    }
    if (!normalized.supportMessage) {
      normalized.supportMessage = defaults.supportMessage ?? "";
    }
  }

  return normalized;
};

const normalizedBusinessPages = (response, capabilityKnown = true) => {
  const source = Array.isArray(response?.businessPages)
    ? response.businessPages
    : [];
  const map = new Map(
    source.map((item) => [
      item.platform,
      normalizePlatformCapabilities(item.platform, item, capabilityKnown),
    ])
  );
  return PLATFORM_ORDER.map(
    (platform) =>
      map.get(platform) || normalizePlatformCapabilities(platform, {}, capabilityKnown)
  );
};

const normalizeSelectionOptions = (response) => {
  const source = Array.isArray(response?.availablePages)
    ? response.availablePages
    : Array.isArray(response?.pages)
      ? response.pages
      : Array.isArray(response?.options)
        ? response.options
        : [];

  return source
    .map((item) => {
      const selectionId =
        item?.selectionId ||
        item?.id ||
        item?.externalUserId ||
        item?.pageId ||
        item?.username;
      if (!selectionId) {
        return null;
      }

      return {
        selectionId: String(selectionId),
        displayName:
          item?.displayName || item?.name || item?.username || "X Account",
        username: item?.username || "",
        pageUrl: item?.pageUrl || item?.businessPageLink || "",
        externalUserId: item?.externalUserId || item?.pageId || "",
      };
    })
    .filter(Boolean);
};

const OrganizationSettings = () => {
  const navigate = useNavigate();
  const { setUserProfile, state: authState } = Auth();
  const orgId = authState?.organizationId || getStoredOrgId() || "";
  const [scimUrlCopied, setScimUrlCopied] = useState(false);
  const [scimTokenCopied, setScimTokenCopied] = useState(false);
  const [samlEntityIdCopied, setSamlEntityIdCopied] = useState(false);
  const [samlAcsUrlCopied, setSamlAcsUrlCopied] = useState(false);

  const getScimBaseUrl = () => {
    let base = process.env.REACT_APP_API_URL_EXTERNAL || platformApiRoot || "";
    if (!base && process.env.REACT_APP_API_URL) {
      base = process.env.REACT_APP_API_URL.replace(/\/v1\/?$/, "");
    }
    if (!base && typeof window !== "undefined") {
      base = window.location.origin;
    }
    return base.replace(/\/+$/, "");
  };

  const scimTenantUrl = orgId ? `${getScimBaseUrl()}/scim/v2/${orgId}` : `${getScimBaseUrl()}/scim/v2`;
  const samlEntityId = orgId ? `${getScimBaseUrl()}/v1/auth/saml/${orgId}/metadata` : `${getScimBaseUrl()}/v1/auth/saml/metadata`;
  const samlAcsUrl = orgId ? `${getScimBaseUrl()}/v1/auth/saml/${orgId}/sso` : `${getScimBaseUrl()}/v1/auth/saml/sso`;
  const [brandingExpanded, setBrandingExpanded] = useState(false);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [orgLogoUrl, setOrgLogoUrl] = useState("");
  const [brandingError, setBrandingError] = useState("");
  const [brandingSuccess, setBrandingSuccess] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadBranding = async () => {
      setBrandingLoading(true);
      try {
        const response = await OrganizationService.getBranding();
        if (mounted && response?.status === true) {
          setOrgName(response?.data?.name || "");
          setOrgLogoUrl(response?.data?.logoUrl || "");
        }
      } catch (error) {
        // Leave defaults — the accordion still renders with blank fields.
      } finally {
        if (mounted) setBrandingLoading(false);
      }
    };
    loadBranding();
    return () => {
      mounted = false;
    };
  }, []);

  const handleBrandingLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBrandingUploading(true);
    setBrandingError("");
    setBrandingSuccess("");
    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("mediaType", "image");
      const response = await uploadMedia(formData);
      if (response?.status === true) {
        const uploadedUrl = response?.mediaUploadData?.mediaDTOS?.[0]?.fileUrl;
        if (uploadedUrl) {
          setOrgLogoUrl(uploadedUrl);
        }
      } else {
        setBrandingError(response?.message || "Failed to upload logo.");
      }
    } catch (error) {
      setBrandingError(error?.response?.data?.message || "Failed to upload logo.");
    } finally {
      setBrandingUploading(false);
      event.target.value = "";
    }
  };

  const handleSaveBranding = async () => {
    setBrandingSaving(true);
    setBrandingError("");
    setBrandingSuccess("");
    try {
      const response = await OrganizationService.updateBranding({
        name: orgName.trim() || undefined,
        logoUrl: orgLogoUrl || undefined,
      });
      if (response?.status === true) {
        setBrandingSuccess("Organization branding updated successfully.");

        try {
          const profileResponse = await UserService.getProfile();
          if (profileResponse?.status === true) {
            setUserProfile(profileResponse?.data);
          }
        } catch (refreshError) {
          // Non-fatal — sidebars will pick up the new branding on next profile fetch.
        }
      } else {
        setBrandingError(response?.message || "Failed to update organization branding.");
      }
    } catch (error) {
      setBrandingError(
        error?.response?.data?.message || "Failed to update organization branding."
      );
    } finally {
      setBrandingSaving(false);
    }
  };

  const [businessPages, setBusinessPages] = useState(() =>
    normalizedBusinessPages(undefined, false)
  );
  const [leaders, setLeaders] = useState([]);
  const initParams = new URLSearchParams(window.location.search);
  const initStatus = initParams.get("businessConnect");
  const initPlatform = initParams.get("platform")?.toUpperCase();
  const initTransactionId = initParams.get("transactionId");
  const isLinkedInSelect = initStatus === "select" && initPlatform === "LINKEDIN" && initTransactionId;

  const [activePlatform, setActivePlatform] = useState(
    isLinkedInSelect ? "LINKEDIN" : (initPlatform === "X" && initStatus === "select" ? "X" : PLATFORM_ORDER[0])
  );
  const [loading, setLoading] = useState(true);
  const [deletingPlatform, setDeletingPlatform] = useState("");
  const [connectingPlatform, setConnectingPlatform] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [leaderErrorMessage, setLeaderErrorMessage] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [businessExpanded, setBusinessExpanded] = useState(!!isLinkedInSelect);
  const [leadersExpanded, setLeadersExpanded] = useState(false);
  const [reconciliationLogs, setReconciliationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [visibleLogsCount, setVisibleLogsCount] = useState(10);
  const [savingManualPlatform, setSavingManualPlatform] = useState("");
  const [selectionTransactionId, setSelectionTransactionId] = useState(isLinkedInSelect ? initTransactionId : "");
  const [selectionOptions, setSelectionOptions] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectingPlatform, setSelectingPlatform] = useState("");
  const [manualLinkedInForm, setManualLinkedInForm] = useState({
    accessToken: "",
    externalUserId: "",
    displayName: "",
    pageUrl: "",
    username: "",
  });

  const [scimExpanded, setScimExpanded] = useState(false);
  const [scimToken, setScimToken] = useState("");
  const [scimLoading, setScimLoading] = useState(false);
  const [scimError, setScimError] = useState("");

  // SAML IdP Configuration State (Tab 0 = Metadata XML Upload, Tab 1 = Manual Form)
  const [samlConfigTab, setSamlConfigTab] = useState(0);
  const [idpSsoUrl, setIdpSsoUrl] = useState("");
  const [idpEntityId, setIdpEntityId] = useState("");
  const [idpCertificate, setIdpCertificate] = useState("");
  const [samlSaveLoading, setSamlSaveLoading] = useState(false);
  const [samlSaveSuccess, setSamlSaveSuccess] = useState("");
  const [samlSaveError, setSamlSaveError] = useState("");
  const [metadataXmlFile, setMetadataXmlFile] = useState(null);

  useEffect(() => {
    if (!scimExpanded || !orgId) return;
    const fetchSamlConfig = async () => {
      try {
        const response = await authService.getSamlConfig(orgId);
        if (response?.data) {
          setIdpSsoUrl(response.data.idpSsoUrl || "");
          setIdpEntityId(response.data.idpEntityId || "");
          setIdpCertificate(response.data.idpCertificate || "");
        }
      } catch (e) {
        // Non-fatal fallback
      }
    };
    fetchSamlConfig();
  }, [scimExpanded, orgId]);

  const handleSaveSamlManualConfig = async () => {
    setSamlSaveLoading(true);
    setSamlSaveError("");
    setSamlSaveSuccess("");
    try {
      const response = await authService.saveSamlConfig(orgId, {
        idpSsoUrl: idpSsoUrl.trim(),
        idpEntityId: idpEntityId.trim(),
        idpCertificate: idpCertificate.trim(),
        enabled: true,
      });
      if (response?.data) {
        setSamlSaveSuccess("SAML Identity Provider configuration saved successfully!");
      }
    } catch (error) {
      setSamlSaveError(error?.response?.data?.message || "Failed to save SAML configuration.");
    } finally {
      setSamlSaveLoading(false);
    }
  };

  const handleUploadMetadataXml = async (event) => {
    const file = event?.target?.files?.[0] || metadataXmlFile;
    if (!file) return;

    setSamlSaveLoading(true);
    setSamlSaveError("");
    setSamlSaveSuccess("");
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const xmlContent = e.target.result;
        try {
          const response = await authService.uploadSamlMetadataXml(orgId, xmlContent);
          if (response?.data) {
            setIdpSsoUrl(response.data.idpSsoUrl || "");
            setIdpEntityId(response.data.idpEntityId || "");
            setIdpCertificate(response.data.idpCertificate || "");
            setSamlSaveSuccess("Metadata XML uploaded & SAML Identity Provider configured successfully!");
          }
        } catch (apiError) {
          const errMsg = apiError?.response?.data?.message || apiError?.response?.data?.error || apiError?.message || "Failed to parse and save Metadata XML.";
          setSamlSaveError(errMsg);
        } finally {
          setSamlSaveLoading(false);
        }
      };
      reader.readAsText(file);
    } catch (fileError) {
      setSamlSaveError("Error reading metadata XML file.");
      setSamlSaveLoading(false);
    }
  };

  const handleGenerateScimToken = async () => {
    setScimLoading(true);
    setScimError("");
    setScimToken("");
    try {
      const response = await authService.generateScimToken();
      if (response && response.status === true) {
        setScimToken(response.accessToken);
      } else {
        setScimError(response?.message || "Failed to generate token.");
      }
    } catch (error) {
      setScimError(error?.response?.data?.message || "Failed to generate token.");
    } finally {
      setScimLoading(false);
    }
  };

  useEffect(() => {
    const handleOnboardingAction = (e) => {
      if (e.detail?.action === "expand-business-pages") {
        setBusinessExpanded(true);
        setTimeout(() => {
          const el = document.getElementById("onboarding-business-pages-accordion");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    };
    window.addEventListener("onboarding-action", handleOnboardingAction);
    return () => window.removeEventListener("onboarding-action", handleOnboardingAction);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadSettings = async () => {
      setLoading(true);
      setErrorMessage("");
      setLeaderErrorMessage("");
      try {
        const [businessResult, leaderResult] = await Promise.allSettled([
          getBusinessPages(),
          getLeaderPages(),
        ]);

        if (!mounted) {
          return;
        }

        if (businessResult.status === "fulfilled") {
          setBusinessPages(normalizedBusinessPages(businessResult.value, true));
        } else {
          const businessError = businessResult.reason;
          if (businessError?.response?.status === 403) {
            setForbidden(true);
            return;
          }
          setErrorMessage("Failed to load business page settings.");
        }

        if (leaderResult.status === "fulfilled") {
          const leaderResponse = leaderResult.value;
          setLeaders(
            Array.isArray(leaderResponse?.leaderPages)
              ? leaderResponse.leaderPages
              : Array.isArray(leaderResponse?.leaders)
                ? leaderResponse.leaders
                : []
          );
        } else {
          const leaderError = leaderResult.reason;
          if (leaderError?.response?.status === 403) {
            setForbidden(true);
            return;
          }
          setLeaders([]);
          setLeaderErrorMessage(
            leaderError?.response?.data?.message ||
            "Unable to load leader status for this organization."
          );
        }
        setForbidden(false);
      } catch (error) {
        if (!mounted) {
          return;
        }
        if (error?.response?.status === 403) {
          setForbidden(true);
        } else {
          setErrorMessage("Failed to load business page settings.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchLogs = async () => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const response = await getReconciliationLogs();
      if (response && response.status) {
        setReconciliationLogs(response.logs || []);
        setVisibleLogsCount(10);
      } else {
        setLogsError(response?.message || "Failed to load reconciliation logs");
      }
    } catch (err) {
      setLogsError("Error fetching reconciliation logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (logsExpanded) {
      fetchLogs();
    }
  }, [logsExpanded]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("businessConnect");
    const platform = params.get("platform");
    const reason = params.get("reason");
    const transactionId = params.get("transactionId");
    if (!status) {
      return;
    }

    const normalizedPlatform = platform?.toUpperCase();
    const label = normalizedPlatform
      ? PLATFORM_LABELS[normalizedPlatform] || platform
      : "Business page";
    if (status === "success") {
      setSuccessMessage(`${label} business page connected successfully.`);
      setErrorMessage("");
    } else if (status === "error") {
      setErrorMessage(
        getBusinessConnectErrorMessage(normalizedPlatform, reason)
      );
      setSuccessMessage("");
    } else if (status === "select") {
      if ((normalizedPlatform === "X" || normalizedPlatform === "LINKEDIN") && transactionId) {
        setActivePlatform(normalizedPlatform);
        setBusinessExpanded(true);
        setSelectionTransactionId(transactionId);
        setSelectionOptions([]);
        setSelectedOptionId("");
        setErrorMessage("");
        setSuccessMessage(
          normalizedPlatform === "LINKEDIN"
            ? "Select the LinkedIn company page to connect as your organization business page."
            : "Confirm the connected X handle to save it as the organization business account."
        );
      } else {
        setErrorMessage(
          `${label} selection is not available in this admin flow.`
        );
        setSuccessMessage("");
      }
    }

    params.delete("businessConnect");
    params.delete("platform");
    params.delete("reason");
    if (status !== "select" || (normalizedPlatform !== "X" && normalizedPlatform !== "LINKEDIN")) {
      params.delete("transactionId");
    }
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""
      }${window.location.hash || ""}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, []);

  useEffect(() => {
    if (!selectionTransactionId || (activePlatform !== "X" && activePlatform !== "LINKEDIN")) {
      return;
    }

    let mounted = true;

    const loadSelectionOptions = async () => {
      setSelectionLoading(true);
      try {
        const response = await getAvailableBusinessPages(activePlatform, selectionTransactionId);
        if (!mounted) {
          return;
        }

        const options = normalizeSelectionOptions(response);
        setSelectionOptions(options);
        setSelectedOptionId((current) =>
          current && options.some((option) => option.selectionId === current)
            ? current
            : options[0]?.selectionId || ""
        );

        if (!options.length) {
          setErrorMessage(
            `${PLATFORM_LABELS[activePlatform] || activePlatform} connection completed, but no business pages were returned for this account.`
          );
        }
      } catch (error) {
        if (!mounted) {
          return;
        }
        setErrorMessage(
          error?.response?.data?.message ||
          `Failed to load available ${PLATFORM_LABELS[activePlatform] || activePlatform} business pages.`
        );
      } finally {
        if (mounted) {
          setSelectionLoading(false);
        }
      }
    };

    loadSelectionOptions();
    return () => {
      mounted = false;
    };
  }, [activePlatform, selectionTransactionId]);

  useEffect(() => {
    if (activePlatform === "X" || activePlatform === "LINKEDIN") {
      return;
    }
    setSelectionTransactionId("");
    setSelectionOptions([]);
    setSelectedOptionId("");
    setSelectionLoading(false);
    setSelectingPlatform("");
  }, [activePlatform]);

  const handleDisconnect = async (platform) => {
    setDeletingPlatform(platform);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await deleteBusinessPage(platform);
      setBusinessPages((current) =>
        current.map((page) =>
          page.platform === platform ? emptyPlatformState(platform, true) : page
        )
      );
      setSuccessMessage(
        `${PLATFORM_LABELS[platform]} business page disconnected.`
      );
      try {
        const freshPages = await getBusinessPages();
        if (freshPages) {
          setBusinessPages(normalizedBusinessPages(freshPages, true));
        }
      } catch (refreshErr) {
        // Local state is already cleanly disconnected
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        `Failed to disconnect ${PLATFORM_LABELS[platform]}.`
      );
    } finally {
      setDeletingPlatform("");
    }
  };

  const handleConnect = async (platform) => {
    if (!hasStoredSessionToken()) {
      setErrorMessage("Your session has expired. Please sign in again to reconnect this business page.");
      setSuccessMessage("");
      navigate("/sign-in");
      return;
    }

    setConnectingPlatform(platform);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await connectBusinessPage(platform);
      if (response?.authUrl) {
        window.location.href = response.authUrl;
        return;
      }
      setErrorMessage(
        `No authorization URL was returned for ${PLATFORM_LABELS[platform]}.`
      );
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        `Failed to start ${PLATFORM_LABELS[platform]} connection.`
      );
    } finally {
      setConnectingPlatform("");
    }
  };

  const clearSelectionFlow = () => {
    setSelectionTransactionId("");
    setSelectionOptions([]);
    setSelectedOptionId("");
    setSelectionLoading(false);
    setSelectingPlatform("");

    const params = new URLSearchParams(window.location.search);
    params.delete("transactionId");
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""
      }${window.location.hash || ""}`;
    window.history.replaceState({}, document.title, nextUrl);
  };

  const handleSelectBusinessPage = async (platform) => {
    if (!selectionTransactionId || !selectedOptionId) {
      setErrorMessage(`Select the ${PLATFORM_LABELS[platform] || platform} business page before continuing.`);
      setSuccessMessage("");
      return;
    }

    setSelectingPlatform(platform);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await selectBusinessPage(platform, {
        transactionId: selectionTransactionId,
        selectionId: selectedOptionId,
      });
      const savedPage = Array.isArray(response?.businessPages)
        ? response.businessPages[0]
        : response?.businessPage || null;

      if (savedPage) {
        setBusinessPages((current) =>
          current.map((page) =>
            page.platform === platform
              ? normalizePlatformCapabilities(platform, savedPage, true)
              : page
          )
        );
      }

      clearSelectionFlow();
      setSuccessMessage(`${PLATFORM_LABELS[platform] || platform} business page connected successfully.`);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        `Failed to connect ${PLATFORM_LABELS[platform] || platform} business page.`
      );
    } finally {
      setSelectingPlatform("");
    }
  };

  const handleManualFieldChange = (field) => (event) => {
    const value = event.target.value;
    setManualLinkedInForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleManualLinkedInSave = async () => {
    if (!manualLinkedInForm.accessToken.trim() || !manualLinkedInForm.externalUserId.trim()) {
      setErrorMessage("LinkedIn organization ID and access token are required for manual company setup.");
      setSuccessMessage("");
      return;
    }

    setSavingManualPlatform("LINKEDIN");
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const response = await saveBusinessPageManual("LINKEDIN", {
        enabled: true,
        accessToken: manualLinkedInForm.accessToken.trim(),
        externalUserId: manualLinkedInForm.externalUserId.trim(),
        displayName: manualLinkedInForm.displayName.trim() || undefined,
        pageUrl: manualLinkedInForm.pageUrl.trim() || undefined,
        username: manualLinkedInForm.username.trim() || undefined,
      });
      const savedPage = Array.isArray(response?.businessPages)
        ? response.businessPages[0]
        : null;
      if (savedPage) {
        setBusinessPages((current) =>
          current.map((page) =>
            page.platform === "LINKEDIN"
              ? normalizePlatformCapabilities("LINKEDIN", savedPage, true)
              : page
          )
        );
      }
      setManualLinkedInForm((current) => ({
        ...current,
        accessToken: "",
      }));
      setSuccessMessage("LinkedIn business page saved with manual company credentials.");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        "Failed to save the LinkedIn business page with manual company credentials."
      );
    } finally {
      setSavingManualPlatform("");
    }
  };

  const renderLeaderStatuses = (leader) => {
    const statusByPlatform = new Map(
      (leader.platforms || []).map((platform) => [platform.platform, platform])
    );

    return PLATFORM_ORDER.map((platform) => {
      const status = statusByPlatform.get(platform);
      const connected = status?.status === "CONNECTED";
      const syncMeta = getLeaderSyncStatusMeta(status);
      return (
        <Card
          key={`${leader.userId}-${platform}`}
          variant="outlined"
          sx={{ p: 1.5, minWidth: 220, borderRadius: 2, borderColor: "#E2E8F0" }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography sx={{ fontWeight: 600 }}>{PLATFORM_LABELS[platform]}</Typography>
              <Chip
                label={connected ? "Connected" : "Not Connected"}
                color={connected ? "success" : "default"}
                variant={connected ? "filled" : "outlined"}
                size="small"
              />
              <Chip
                label={syncMeta.label}
                color={syncMeta.color}
                variant={syncMeta.variant}
                size="small"
              />
            </Stack>
            <Typography sx={{ fontSize: 12, color: "#64748B" }}>
              Last success: {formatSyncDateTime(status?.lastSyncSuccessAt)}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748B" }}>
              Last attempt: {formatSyncDateTime(status?.lastSyncAttemptAt)}
            </Typography>
            {status?.lastSyncStatus === "FAILED" && status?.lastSyncError ? (
              <Typography sx={{ fontSize: 12, color: "#B42318", fontWeight: 500 }}>
                {status.lastSyncError}
              </Typography>
            ) : status?.lastSyncStatus === "SUCCESS_NO_NEW_CONTENT" ? (
              <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                Connection is healthy, but no new posts were imported in the current fetch window.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                {connected
                  ? "Leader content sync is tracked per platform."
                  : "Leader must connect this platform before sync can run."}
              </Typography>
            )}
          </Stack>
        </Card>
      );
    });
  };

  const activePage =
    businessPages.find((page) => page.platform === activePlatform) ||
    emptyPlatformState(activePlatform, true);
  const hasCapabilityData = typeof activePage.oauthSupported === "boolean";
  const supportsOauth = activePage.oauthSupported === true;
  const supportsManualFallback = activePage.manualFallbackAvailable === true;
  const oauthProviderLabel = activePage.oauthProviderLabel || "OAuth";
  const syncStatusMeta = getSyncStatusMeta(activePage);
  const busy =
    deletingPlatform === activePage.platform ||
    connectingPlatform === activePage.platform ||
    savingManualPlatform === activePage.platform;

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{ mt: 1, p: 4, borderRadius: 3, backgroundColor: "#FFFFFF" }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography sx={{ color: "#6B7280" }}>
            Loading organization settings...
          </Typography>
        </Stack>
      </Card>
    );
  }

  if (forbidden) {
    return (
      <Card
        elevation={0}
        sx={{ mt: 1, p: 4, borderRadius: 3, backgroundColor: "#FFFFFF" }}
      >
        <Alert severity="info">
          Only organization admins can view and manage business page settings.
          Leader connections remain personal and are managed by each leader from
          the regular social media integration page.
        </Alert>
      </Card>
    );
  }

  const platformSupportMessage = supportsOauth
    ? activePage.platform === "FACEBOOK" || activePage.platform === "INSTAGRAM"
      ? "Use the shared Meta app to connect the organization-owned page. The selected page token stays organization-owned and separate from employee personal integrations."
      : activePage.supportMessage
    : hasCapabilityData
      ? activePage.supportMessage ||
      "Save the organization-owned page metadata and access credentials used for business-page fetch and publishing."
      : "Connection options will appear after business page settings finish loading.";

  const capabilityAlertSeverity = supportsOauth
    ? "info"
    : hasCapabilityData
      ? "warning"
      : "info";

  const capabilityAlertMessage = supportsOauth
    ? `${PLATFORM_LABELS[activePage.platform]} business pages use the shared ${oauthProviderLabel} app. Leaders and employees still connect their own personal pages from the regular integration section.`
    : hasCapabilityData
      ? activePage.supportMessage ||
      "OAuth connection is not available for this platform yet. Use Advanced manual setup for now."
      : "Business page settings are still loading. Refresh this page if connection options do not appear.";

  return (
    <Stack spacing={0} sx={{ gap: 2 }}>
      <Accordion
        expanded={brandingExpanded}
        onChange={(_, expanded) => setBrandingExpanded(expanded)}
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2D76DC", fontSize: "18px" }} />}
          sx={accordionSummarySx}
        >
          <div className="reset-password">
            <div>
              <Typography variant="subtitle1" className="sub-title">
                Organization Branding
              </Typography>
              <Typography variant="body2" className="mini-title">
                Customize the logo and name shown across your organization's workspace.
              </Typography>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={accordionDetailsSx}>
          {brandingError && <Alert severity="error" sx={{ mb: 2 }}>{brandingError}</Alert>}
          {brandingSuccess && <Alert severity="success" sx={{ mb: 2 }}>{brandingSuccess}</Alert>}

          {brandingLoading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Stack spacing={0} sx={{ pt: 1 }}>
              {/* Logo Row */}
              <Box sx={{ borderBottom: "1px solid #E2E8F0", py: 3 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 600, color: "#0F172A", fontSize: "15px", mb: 0.5 }}>
                      Company Logo
                    </Typography>
                    <Typography sx={{ color: "#64748B", fontSize: "13.5px", lineHeight: 1.5 }}>
                      This logo will be displayed on your workspace and emails.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center">
                      <Box sx={{ flexShrink: 0, position: "relative", "&:hover .overlay": { opacity: 1 }, cursor: "pointer", display: "inline-block" }}>
                        <Avatar
                          src={orgLogoUrl || logoImg}
                          variant="rounded"
                          sx={{ 
                            width: { xs: 100, sm: 120 },
                            height: { xs: 75, sm: 90 }, 
                            borderRadius: "12px",
                            border: "1px solid #E2E8F0",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                            backgroundColor: "#FFFFFF",
                            transition: "all 0.3s ease",
                            "& img": { objectFit: "contain", p: 1 },
                            "&:hover": {
                              boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                            }
                          }}
                        />
                        <Box 
                          className="overlay"
                          component="label"
                          sx={{
                            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            borderRadius: "12px",
                            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                            opacity: 0, transition: "opacity 0.2s ease",
                            color: "white"
                          }}
                        >
                          <CameraAltIcon sx={{ fontSize: 28, mb: 0.5 }} />
                          <Typography sx={{ fontSize: "11px", fontWeight: 600 }}>Update</Typography>
                          <input type="file" accept="image/*" hidden onChange={handleBrandingLogoUpload} disabled={brandingUploading} />
                        </Box>
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                          <Button 
                            variant="outlined" 
                            component="label" 
                            disabled={brandingUploading}
                            startIcon={<CloudUploadIcon />}
                            sx={{ 
                              textTransform: "none", borderColor: "#CBD5E1", color: "#334155", 
                              fontWeight: 600, borderRadius: "8px", px: { xs: 1.5, sm: 2 }, py: 0.75,
                              fontSize: { xs: "13px", sm: "14px" },
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              transition: "all 0.2s ease",
                              "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#94A3B8", transform: "translateY(-1px)" }
                            }}
                          >
                            {brandingUploading ? "Uploading..." : "Upload Logo"}
                            <input type="file" accept="image/*" hidden onChange={handleBrandingLogoUpload} />
                          </Button>
                        </Stack>
                        <Typography sx={{ fontSize: { xs: 11.5, sm: 13 }, color: "#64748B", lineHeight: 1.3 }}>
                          Recommended size: 80x50px. Max size 2MB.
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Name Row */}
              <Box sx={{ borderBottom: "1px solid #E2E8F0", py: 4 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 600, color: "#0F172A", fontSize: "15px", mb: 0.5 }}>
                      Organization Name
                    </Typography>
                    <Typography sx={{ color: "#64748B", fontSize: "13.5px", lineHeight: 1.5 }}>
                      The display name of your company or workspace.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      placeholder="e.g. Social Ripple"
                      value={orgName}
                      onChange={(event) => setOrgName(event.target.value)}
                      variant="outlined"
                      sx={{
                        maxWidth: "420px", width: "100%",
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#F8FAFC",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          "fieldset": { borderColor: "#E2E8F0" },
                          "&:hover fieldset": { borderColor: "#CBD5E1" },
                          "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "2px" },
                          "&.Mui-focused": { backgroundColor: "#FFFFFF", boxShadow: "0 0 0 4px rgba(37,99,235,0.1)" }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Footer Action Row */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 3 }}>
                <Button
                  variant="contained"
                  disabled={brandingSaving || brandingUploading}
                  onClick={handleSaveBranding}
                  sx={{ 
                    backgroundColor: "#0047AB", color: "#FFFFFF", 
                    px: 3, py: 1, textTransform: "none", fontWeight: 500,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
                    "&:hover": { backgroundColor: "#003380" },
                    "&:disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
                  }}
                >
                  {brandingSaving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        id="onboarding-business-pages-accordion"
        expanded={businessExpanded}
        onChange={(_, expanded) => setBusinessExpanded(expanded)}
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2D76DC", fontSize: "18px" }} />}
          sx={accordionSummarySx}
        >
          <div className="reset-password">
            <div>
              <Typography variant="subtitle1" className="sub-title">
                Business Pages
              </Typography>
              <Typography variant="body2" className="mini-title">
                Manage organization-owned Facebook, Instagram, LinkedIn, and X
                pages in one place.
              </Typography>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={accordionDetailsSx}>
          {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
          )}

          <Box sx={{ borderBottom: "1px solid #E2E8F0", mb: 3 }}>
            <Tabs
              value={activePlatform}
              onChange={(_, value) => setActivePlatform(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  minHeight: 48,
                  fontWeight: 600,
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  color: "#0047AB",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#0047AB",
                },
              }}
            >
              {PLATFORM_ORDER.map((platform) => (
                <Tab
                  key={platform}
                  value={platform}
                  label={PLATFORM_LABELS[platform]}
                />
              ))}
            </Tabs>
          </Box>

          <Card
            variant="outlined"
            sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 3, borderColor: "#E2E8F0" }}
          >
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {PLATFORM_LABELS[activePage.platform]}
                  </Typography>
                  <Chip
                    label={activePage.connected ? "Connected" : "Not Connected"}
                    color={activePage.connected ? "success" : "default"}
                    variant={activePage.connected ? "filled" : "outlined"}
                  />
                  <Chip
                    label={syncStatusMeta.label}
                    color={syncStatusMeta.color}
                    variant={syncStatusMeta.variant}
                  />
                </Stack>
                <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                  {platformSupportMessage}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Chip
                  label={activePage.enabled ? "Fetch Enabled" : "Fetch Disabled"}
                  color={activePage.enabled ? "success" : "default"}
                  variant={activePage.enabled ? "filled" : "outlined"}
                  size="small"
                />
                {activePage.displayName ? (
                  <Chip
                    label={activePage.displayName}
                    variant="outlined"
                    size="small"
                  />
                ) : null}
              </Stack>
            </Stack>

            <Alert severity={capabilityAlertSeverity} sx={{ mb: 2 }}>
              {capabilityAlertMessage}
            </Alert>

            <Card
              variant="outlined"
              sx={{
                p: { xs: 1.5, md: 2 },
                mb: 2,
                borderRadius: 2,
                borderColor: "#E2E8F0",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                  Sync Health
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      Last successful sync
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatSyncDateTime(activePage.lastSyncSuccessAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      Last attempt
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatSyncDateTime(activePage.lastSyncAttemptAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      Latest error time
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatSyncDateTime(activePage.lastSyncErrorAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      Fetch cadence
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {activePage.scheduleCadenceLabel || "Not scheduled"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      Next scheduled fetch
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatSyncDateTime(activePage.nextScheduledFetchAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4} />
                </Grid>
                {activePage.connected && typeof activePage.lastImportedCount === "number" ? (
                  <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                    {activePage.lastImportedCount > 0
                      ? `Last import inserted ${activePage.lastImportedCount} post${activePage.lastImportedCount === 1 ? "" : "s"
                      }.`
                      : "No new posts were imported in the latest fetch window."}
                  </Typography>
                ) : null}
                {activePage.lastSyncStatus === "FAILED" && activePage.lastSyncError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {activePage.lastSyncError}
                  </Alert>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                    {activePage.connected
                      ? "This platform triggers an immediate fetch after a successful connection and continues syncing on the scheduler."
                      : "Connect this platform to start an immediate fetch, scheduled syncs, and health tracking."}
                  </Typography>
                )}
              </Stack>
            </Card>

            {/* Manual LinkedIn setup — commented out, enable if client requests
            {activePage.platform === "LINKEDIN" && supportsManualFallback && !activePage.connected && (
              <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: "#E2E8F0", backgroundColor: "#FFFDF7" }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>Manual Company Fallback</Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>Use this only when LinkedIn OAuth discovery cannot return your company page.</Typography>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}><TextField fullWidth label="LinkedIn Organization ID" value={manualLinkedInForm.externalUserId} onChange={handleManualFieldChange("externalUserId")} placeholder="12345678" /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Display Name" value={manualLinkedInForm.displayName} onChange={handleManualFieldChange("displayName")} placeholder="Moonhive Pvt Ltd" /></Grid>
                    <Grid item xs={12}><TextField fullWidth label="Access Token" value={manualLinkedInForm.accessToken} onChange={handleManualFieldChange("accessToken")} multiline minRows={3} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Company Page URL" value={manualLinkedInForm.pageUrl} onChange={handleManualFieldChange("pageUrl")} placeholder="https://www.linkedin.com/company/your-company" /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Company Vanity Name" value={manualLinkedInForm.username} onChange={handleManualFieldChange("username")} placeholder="your-company" /></Grid>
                  </Grid>
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" sx={{ borderColor: "#0047AB", color: "#0047AB" }} disabled={busy} onClick={handleManualLinkedInSave}>
                      {savingManualPlatform === "LINKEDIN" ? "Saving..." : activePage.connected ? "Save Manual Company Credentials" : "Use Manual Company Credentials"}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            )}
            */}

            {(activePage.platform === "X" || activePage.platform === "LINKEDIN") && selectionTransactionId && (
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  borderColor: "#E2E8F0",
                  backgroundColor: "#FFFDF7",
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                      {activePage.platform === "LINKEDIN" ? "Select LinkedIn Company Page" : "Confirm Connected X Handle"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                      {activePage.platform === "LINKEDIN"
                        ? "Select the LinkedIn company page you want to connect as your organization business page."
                        : "Confirm that the connected handle below is the organization-owned brand account before saving it."}
                    </Typography>
                  </Box>

                  {selectionLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                        {activePage.platform === "LINKEDIN" ? "Loading LinkedIn company pages..." : "Loading connected X account..."}
                      </Typography>
                    </Stack>
                  ) : selectionOptions.length === 0 && selectionTransactionId ? (
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                        Pages not loaded yet. Click below to fetch available pages.
                      </Typography>
                      <Button
                        variant="outlined"
                        sx={{ borderColor: "#94A3B8", color: "#64748B", textTransform: "none" }}
                        disabled={selectionLoading}
                        onClick={async () => {
                          setSelectionLoading(true);
                          try {
                            const response = await getAvailableBusinessPages(activePage.platform, selectionTransactionId);
                            const options = normalizeSelectionOptions(response);
                            setSelectionOptions(options);
                            setSelectedOptionId(options[0]?.selectionId || "");
                            if (!options.length) {
                              setErrorMessage("No business pages found for this transaction.");
                            }
                          } catch (error) {
                            setErrorMessage(error?.response?.data?.message || "Failed to load available pages.");
                          } finally {
                            setSelectionLoading(false);
                          }
                        }}
                      >
                        Load Available Pages
                      </Button>
                    </Stack>
                  ) : selectionOptions.length > 0 ? (
                    <Stack spacing={1.5}>
                      <TextField
                        select
                        fullWidth
                        label={activePage.platform === "LINKEDIN" ? "Select Company Page" : "Connected X Handle"}
                        value={selectedOptionId}
                        onChange={(event) => setSelectedOptionId(event.target.value)}
                        SelectProps={{ native: true }}
                      >
                        {selectionOptions.map((option) => (
                          <option key={option.selectionId} value={option.selectionId}>
                            {option.displayName}
                            {option.username ? ` (@${option.username})` : ""}
                          </option>
                        ))}
                      </TextField>

                      {selectionOptions
                        .filter((option) => option.selectionId === selectedOptionId)
                        .map((option) => (
                          <Stack key={option.selectionId} spacing={0.5}>
                            {option.username ? (
                              <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                                Handle: @{option.username}
                              </Typography>
                            ) : null}
                            {option.externalUserId ? (
                              <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                                Account ID: {option.externalUserId}
                              </Typography>
                            ) : null}
                            {option.pageUrl ? (
                              <Typography sx={{ fontSize: 13, color: "#64748B", wordBreak: "break-all" }}>
                                Profile URL: {option.pageUrl}
                              </Typography>
                            ) : null}
                          </Stack>
                        ))}
                    </Stack>
                  ) : (
                    <Alert severity="warning">
                      No business pages are available to select for this transaction.
                    </Alert>
                  )}

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#0047AB" }}
                      disabled={
                        selectionLoading ||
                        selectingPlatform === activePage.platform ||
                        !selectedOptionId
                      }
                      onClick={() => handleSelectBusinessPage(activePage.platform)}
                    >
                      {selectingPlatform === activePage.platform
                        ? "Saving..."
                        : activePage.platform === "LINKEDIN"
                          ? "Connect LinkedIn Company Page"
                          : "Confirm and Connect X Account"}
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ borderColor: "#94A3B8", color: "#64748B" }}
                      disabled={selectionLoading || selectingPlatform === activePage.platform}
                      onClick={clearSelectionFlow}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            )}

            <Card
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                borderColor: "#E2E8F0",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, color: "#64748B" }}>Connected page</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {activePage.displayName || activePage.username || "Not connected"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 12, color: "#64748B" }}>Page identifier</Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {activePage.pageId || activePage.externalUserId || "Not available"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 12, color: "#64748B" }}>Business page link</Typography>
                  <Typography sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                    {activePage.businessPageLink || activePage.pageUrl || "Not available"}
                  </Typography>
                </Grid>
              </Grid>
            </Card>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 3, alignItems: "stretch", width: "100%" }}>
              {supportsOauth ? (
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#0047AB" }}
                  disabled={busy}
                  onClick={() => handleConnect(activePage.platform)}
                >
                  {connectingPlatform === activePage.platform
                    ? "Redirecting..."
                    : activePage.connected
                      ? `Reconnect with ${oauthProviderLabel}`
                      : `Connect with ${oauthProviderLabel}`}
                </Button>
              ) : hasCapabilityData ? (
                <Button
                  variant="outlined"
                  sx={{ borderColor: "#94A3B8", color: "#64748B" }}
                  disabled
                >
                  {activePage.platform === "LINKEDIN" && supportsManualFallback
                    ? "Manual Setup Only"
                    : "Connection Unavailable"}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  sx={{ borderColor: "#94A3B8", color: "#64748B" }}
                  disabled
                >
                  Loading Connection Options
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                disabled={busy || !activePage.connected}
                onClick={() => handleDisconnect(activePage.platform)}
                sx={{}}
              >
                {deletingPlatform === activePage.platform
                  ? "Disconnecting..."
                  : "Disconnect"}
              </Button>
            </Stack>
          </Card>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={leadersExpanded}
        onChange={(_, expanded) => setLeadersExpanded(expanded)}
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2D76DC", fontSize: "18px" }} />}
          sx={accordionSummarySx}
        >
          <div className="reset-password">
            <div>
              <Typography variant="subtitle1" className="sub-title">
                Leader Page Connection Status
              </Typography>
              <Typography variant="body2" className="mini-title">
                Review leader-owned social connections without editing personal
                accounts.
              </Typography>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={accordionDetailsSx}>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Typography sx={{ color: "#6B7280" }}>
              Leaders appear here automatically based on the user role flags.
              This section is read-only: leaders manage their own personal
              social connections from the regular integration flow.
            </Typography>
          </Stack>

          {leaderErrorMessage ? (
            <Alert severity="warning">{leaderErrorMessage}</Alert>
          ) : leaders.length === 0 ? (
            <Alert severity="info">
              No leaders are currently marked for this organization.
            </Alert>
          ) : (
            <Stack spacing={2.5}>
              {leaders.map((leader, index) => (
                <Box key={leader.userId}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {leader.name || leader.email}
                      </Typography>
                      <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                        {leader.email}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {renderLeaderStatuses(leader)}
                    </Stack>
                  </Stack>
                  {index < leaders.length - 1 && <Divider sx={{ mt: 2.5 }} />}
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={logsExpanded}
        onChange={(_, expanded) => setLogsExpanded(expanded)}
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2D76DC", fontSize: "18px" }} />}
          sx={accordionSummarySx}
        >
          <div className="reset-password">
            <div>
              <Typography variant="subtitle1" className="sub-title">
                Sync Reconciliation Logs
              </Typography>
              <Typography variant="body2" className="mini-title">
                Monitor and verify LinkedIn sync history, including fetched posts count, saved posts, and health status.
              </Typography>
            </div>
          </div>
        </AccordionSummary>

        <AccordionDetails sx={accordionDetailsSx}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }} gap={1}>
            <Typography sx={{ color: "#6B7280" }}>
              Reconciliation records showing posts available vs. saved on LinkedIn.
            </Typography>
            <IconButton onClick={fetchLogs} disabled={logsLoading} color="primary" size="small">
              <RefreshIcon />
            </IconButton>
          </Stack>

          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : logsError ? (
            <Alert severity="error">{logsError}</Alert>
          ) : reconciliationLogs.length === 0 ? (
            <Alert severity="info">No reconciliation logs found for this organization.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #E5E7EB", borderRadius: "8px", overflowX: "auto" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Sync Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Platform</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Profile</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }} align="center">Telemetry</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reconciliationLogs.slice(0, visibleLogsCount).map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatSyncDateTime(log.syncTime)}
                        </TableCell>
                        <TableCell>
                          <Chip label={log.platform} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "#4B5563" }}>
                          {log.syncType === "BUSINESS_PAGE" ? "Business Page" : "Leader Profile"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>
                          {log.profileName || log.profileIdentifier || "N/A"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 13 }}>
                          <Tooltip title={`Saved: ${log.postsSaved} | Fetched: ${log.postsFetched} | Available: ${log.postsAvailable}`}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {log.postsSaved} / {log.postsFetched} / {log.postsAvailable}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            size="small"
                            color={log.status === "SUCCESS" ? "success" : "error"}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={log.details || ""}>
                            <Typography sx={{ fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                              {log.details || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {visibleLogsCount < reconciliationLogs.length && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setVisibleLogsCount((prev) => prev + 10)}
                    sx={{ borderColor: "#E5E7EB", color: "#374151" }}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={scimExpanded}
        onChange={(_, expanded) => setScimExpanded(expanded)}
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#2D76DC", fontSize: "18px" }} />}
          sx={accordionSummarySx}
        >
          <div className="reset-password">
            <div>
              <Typography variant="subtitle1" className="sub-title">
                SAML SSO & SCIM Provisioning (Okta / Azure AD)
              </Typography>
              <Typography variant="body2" className="mini-title">
                Configure Single Sign-On (SAML 2.0) and automatic user provisioning (SCIM) via Identity Providers.
              </Typography>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          <Stack spacing={3} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ color: "#6B7280", mb: 1.5 }}>
                To connect SocialRipple with your Identity Provider (like Microsoft Entra ID / Azure AD or Okta), copy the <strong>SAML SSO URLs</strong> and <strong>SCIM Credentials</strong> below into your Enterprise Application settings.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                href="https://entra.microsoft.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#0078D4",
                  color: "#0078D4",
                  "&:hover": {
                    borderColor: "#005A9E",
                    backgroundColor: "rgba(0, 120, 212, 0.04)"
                  }
                }}
              >
                Open Microsoft Entra Portal
              </Button>
            </Box>

            <Divider />

            {/* SAML SSO Configuration */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827", mb: 0.5 }}>
                SAML 2.0 Single Sign-On (SSO) Settings
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 2 }}>
                Paste these URLs into your Identity Provider's <strong>Basic SAML Configuration</strong> section.
              </Typography>

              {/* Identifier (Entity ID) */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: 0.5 }}>
                  Identifier (Entity ID)
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 1 }}>
                  Paste this into your Identity Provider's <strong>Identifier (Entity ID)</strong> field.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: "stretch" }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={samlEntityId}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", fontSize: 13, backgroundColor: "#f9fafb" }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
                  />
                  <Button
                    variant="outlined"
                    sx={{ whiteSpace: "nowrap", height: "40px", minWidth: "120px" }}
                    onClick={() => {
                      navigator.clipboard.writeText(samlEntityId);
                      setSamlEntityIdCopied(true);
                      setTimeout(() => setSamlEntityIdCopied(false), 2500);
                    }}
                  >
                    {samlEntityIdCopied ? "Copied!" : "Copy URL"}
                  </Button>
                </Box>
              </Box>

              {/* Reply URL (ACS URL) */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: 0.5 }}>
                  Reply URL (Assertion Consumer Service / ACS URL)
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 1 }}>
                  Paste this into your Identity Provider's <strong>Reply URL (ACS URL)</strong> field.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: "stretch" }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={samlAcsUrl}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", fontSize: 13, backgroundColor: "#f9fafb" }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
                  />
                  <Button
                    variant="outlined"
                    sx={{ whiteSpace: "nowrap", height: "40px", minWidth: "120px" }}
                    onClick={() => {
                      navigator.clipboard.writeText(samlAcsUrl);
                      setSamlAcsUrlCopied(true);
                      setTimeout(() => setSamlAcsUrlCopied(false), 2500);
                    }}
                  >
                    {samlAcsUrlCopied ? "Copied!" : "Copy URL"}
                  </Button>
                </Box>
              </Box>

              {/* IdP Configuration Selection Tabs */}
              <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: "#fafafa", borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827", mb: 1 }}>
                  Configure Identity Provider (Azure AD / Okta) Details
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 2 }}>
                  Select how you want to configure your Identity Provider details into SocialRipple:
                </Typography>

                <Tabs
                  value={samlConfigTab}
                  onChange={(_, val) => {
                    setSamlConfigTab(val);
                    setSamlSaveSuccess("");
                    setSamlSaveError("");
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTab-root": {
                      minWidth: "auto",
                      px: { xs: 1.5, sm: 2 },
                      fontSize: { xs: "13px", sm: "14px" },
                    },
                  }}
                >
                  <Tab label="Option 1: Upload Metadata XML" sx={{ textTransform: "none", fontWeight: 600 }} />
                  <Tab label="Option 2: Manual Input Fields" sx={{ textTransform: "none", fontWeight: 600 }} />
                </Tabs>

                {samlSaveSuccess && <Alert severity="success" sx={{ mb: 2 }}>{samlSaveSuccess}</Alert>}
                {samlSaveError && <Alert severity="error" sx={{ mb: 2 }}>{samlSaveError}</Alert>}

                {/* Tab 0: Upload Metadata XML */}
                {samlConfigTab === 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ color: "#4B5563", mb: 1.5 }}>
                      Download the <strong>Federation Metadata XML</strong> file from your Azure AD / Okta portal and upload it here for 1-click automatic setup:
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUploadIcon />}
                        sx={{ textTransform: "none", fontWeight: 600, height: "42px" }}
                      >
                        Select Metadata XML File
                        <input
                          type="file"
                          accept=".xml,text/xml"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            setMetadataXmlFile(file || null);
                            if (file) handleUploadMetadataXml(e);
                          }}
                        />
                      </Button>
                      <Typography variant="caption" sx={{ color: "#6B7280" }}>
                        {metadataXmlFile ? metadataXmlFile.name : "No file selected (.xml)"}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {/* Tab 1: Manual Input Fields */}
                {samlConfigTab === 1 && (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151", display: "block", mb: 0.5 }}>
                        IdP Single Sign-On Service URL (SSO Endpoint)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="https://login.microsoftonline.com/{tenant-id}/saml2"
                        value={idpSsoUrl}
                        onChange={(e) => setIdpSsoUrl(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151", display: "block", mb: 0.5 }}>
                        IdP Entity ID (Issuer / Microsoft Entra Identifier)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="https://sts.windows.net/{tenant-id}/"
                        value={idpEntityId}
                        onChange={(e) => setIdpEntityId(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#374151", display: "block", mb: 0.5 }}>
                        X.509 Public Certificate (Base64 / PEM)
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="-----BEGIN CERTIFICATE----- &#10;MIIC8jCC... &#10;-----END CERTIFICATE-----"
                        value={idpCertificate}
                        onChange={(e) => setIdpCertificate(e.target.value)}
                        InputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
                      />
                    </Box>
                    <Box>
                      <Button
                        variant="contained"
                        disabled={samlSaveLoading}
                        onClick={handleSaveSamlManualConfig}
                        sx={{ backgroundColor: "#0047AB", textTransform: "none", fontWeight: 600 }}
                      >
                        {samlSaveLoading ? "Saving..." : "Save SAML Configuration"}
                      </Button>
                    </Box>
                  </Stack>
                )}
              </Paper>
            </Box>

            <Divider />

            {/* SCIM User Provisioning */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827", mb: 0.5 }}>
                SCIM User Provisioning Settings
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 2 }}>
                Configure automatic cross-domain user identity provisioning via SCIM v2.0.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: 0.5 }}>
                Tenant URL (SCIM Base URL)
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 1 }}>
                Paste this into your Identity Provider's <strong>Tenant URL</strong> configuration field.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: "stretch" }}>
                <TextField
                  fullWidth
                  size="small"
                  value={scimTenantUrl}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: "monospace", fontSize: 13, backgroundColor: "#f9fafb" }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  sx={{
                    whiteSpace: "nowrap",
                    height: "40px",
                    minWidth: "120px",
                    boxSizing: "border-box"
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(scimTenantUrl);
                    setScimUrlCopied(true);
                    setTimeout(() => setScimUrlCopied(false), 2500);
                  }}
                >
                  {scimUrlCopied ? "Copied!" : "Copy URL"}
                </Button>
              </Box>
            </Box>

            <Divider />

            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#374151", mb: 0.5 }}>
                Secret Token (Bearer Token)
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 1 }}>
                Click below to generate a long-lived Bearer token. For security reasons, the token is only displayed once upon generation.
              </Typography>

              {scimError && <Alert severity="error" sx={{ my: 1.5 }}>{scimError}</Alert>}

              {scimToken ? (
                <Box sx={{ mt: 1.5 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Token generated successfully! Copy it now.
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={scimToken}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", fontSize: 12, backgroundColor: "#f9fafb" }
                    }}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                    <Button 
                      variant="contained"
                      sx={{ backgroundColor: "#0047AB" }}
                      onClick={() => {
                        navigator.clipboard.writeText(scimToken);
                        setScimTokenCopied(true);
                        setTimeout(() => setScimTokenCopied(false), 2500);
                      }}
                    >
                      {scimTokenCopied ? "Copied Token!" : "Copy Token"}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={scimLoading}
                      onClick={handleGenerateScimToken}
                    >
                      {scimLoading ? "Generating..." : "Regenerate Token"}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: "#0047AB" }}
                    disabled={scimLoading}
                    onClick={handleGenerateScimToken}
                  >
                    {scimLoading ? "Generating..." : "Generate New SCIM Token"}
                  </Button>
                </Box>
              )}
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default OrganizationSettings;
